import React from 'react';
import {
  Search,
  CheckCircle,
  Hourglass,
  CheckCheck,
  Users
} from 'lucide-react';
import type { Task } from '../../types';

interface WorkflowPipelineProps {
  tasks: Task[];
  selectedTask: number | null;
  onSelectTask: (taskNumber: number) => void;
}

// Map task numbers to icons and colors - NEW 4-STATE WORKFLOW
const taskConfig: Record<number, { icon: React.ElementType; color: string; bgColor: string }> = {
  1: { icon: Search, color: 'text-blue-600', bgColor: 'bg-blue-100' }, // Contrôle administratif
  2: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' }, // Soumission institutions
  3: { icon: Hourglass, color: 'text-amber-600', bgColor: 'bg-amber-100' }, // Attente réponses
  4: { icon: CheckCheck, color: 'text-emerald-600', bgColor: 'bg-emerald-100' }, // Dossier clôturé
};

const WorkflowPipeline: React.FC<WorkflowPipelineProps> = ({
  tasks,
  selectedTask,
  onSelectTask
}) => {
  // Sort tasks by their display order (1, 2, 3, 4)
  const sortedTasks = [...tasks].sort((a, b) => a.task_number - b.task_number);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Pipeline de workflow</h2>

      {/* Pipeline Steps */}
      <div className="relative">
        {/* Connection Line */}
        <div className="absolute top-8 left-8 right-8 h-1 bg-gray-200 hidden md:block" />

        {/* Steps */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sortedTasks.map((task) => {
            const config = taskConfig[task.task_number] || taskConfig[1];
            const Icon = config.icon;
            const isSelected = selectedTask === task.task_number;
            const count = task.customers_count || 0;

            return (
              <button
                key={task.id}
                onClick={() => onSelectTask(task.task_number)}
                className={`relative flex flex-col items-center p-3 rounded-xl transition-all duration-200 ${
                  isSelected 
                    ? `${config.bgColor} ring-2 ring-offset-2 ring-${config.color.replace('text-', '')}` 
                    : 'hover:bg-gray-50'
                }`}
              >
                {/* Icon Container */}
                <div 
                  className={`relative z-10 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isSelected 
                      ? `${config.bgColor} ${config.color}` 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  
                  {/* Badge */}
                  {count > 0 && (
                    <span 
                      className={`absolute -top-1 -right-1 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
                        isSelected 
                          ? 'bg-white text-gray-900 shadow-md' 
                          : `${config.bgColor} ${config.color}`
                      }`}
                    >
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>

                {/* Label */}
                <span 
                  className={`mt-2 text-xs font-medium text-center leading-tight ${
                    isSelected ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  {task.name}
                </span>

                {/* State Number */}
                <span className="text-[10px] text-gray-400 mt-1">
                  État {task.task_number}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center text-gray-500">
          <Users className="w-4 h-4 mr-2" />
          <span className="text-sm">
            Total: <strong className="text-gray-900">{tasks.reduce((sum, t) => sum + (t.customers_count || 0), 0)}</strong> clients
          </span>
        </div>
        {selectedTask && (
          <span className="text-sm text-primary-600 font-medium">
            État {selectedTask} sélectionné
          </span>
        )}
      </div>
    </div>
  );
};

export default WorkflowPipeline;
